import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, EnvelopeSimple, Lock, Eye, EyeSlash, Globe } from '@phosphor-icons/react';

export default function Register() {
  const { t, language, toggleLanguage } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('Kayıt başarılı');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split min-h-screen">
      {/* Left side - Form */}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-12 bg-background">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-foreground">
              Kayıt Ol
            </h1>
            <p className="mt-3 text-muted-foreground">
              Yeni bir hesap oluşturun
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-muted-foreground">
                Ad Soyad
              </Label>
              <div className="relative">
                <User 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ahmet Yılmaz"
                  className="h-12 pl-12 input-enhanced"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                E-posta Adresi
              </Label>
              <div className="relative">
                <EnvelopeSimple 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@restoran.com"
                  className="h-12 pl-12 input-enhanced"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                Şifre
              </Label>
              <div className="relative">
                <Lock 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 pr-12 input-enhanced"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-widest text-muted-foreground">
                Şifre Tekrar
              </Label>
              <div className="relative">
                <Lock 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 input-enhanced"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md btn-primary"
              data-testid="register-submit"
            >
              {loading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Zaten hesabınız var mı?{' '}
            <Link 
              to="/login" 
              className="text-primary hover:underline font-medium"
              data-testid="login-link"
            >
              Giriş Yapın
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="login-image-side relative hidden md:block">
        <img
          src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80"
          alt="Restaurant kitchen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/60 to-foreground/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white">
            <p className="font-heading text-2xl lg:text-3xl font-light leading-relaxed">
              "Her başarılı işletme, doğru araçlarla başlar."
            </p>
            <footer className="mt-4 text-white/70 text-sm uppercase tracking-widest">
              — Stok Yönetim Sistemi
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
